import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Hls from "hls.js";

import "./cameraOnBoarding.css";
import ChaloButton from "../../../../shared/widgets/chaloButton";
import ChaloTopNav from "../../../../shared/widgets/chaloTopNav";
import ChaloLoader from "../../../../shared/widgets/chaloLoader";
import { AuthenticationService } from "../../../../services/authorization";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;
const WATCHDOG_TIMEOUT_MS = 5000;
const DEFAULT_ERROR = "RTMP feed not available due to server issue.";

const useHlsPlayer = (videoRef, retryCount, setRetryCount, setErrorMessage, setIsLiveFeedActive, setIsLoading) => {
  const hlsRef = useRef(null);
  const networkErrorTimeoutRef = useRef(null);
  const watchdogTimeoutRef = useRef(null);

  const clearWatchdog = useCallback(() => {
    if (watchdogTimeoutRef.current) {
      clearTimeout(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, []);

  const cleanupPlayer = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    
    if (networkErrorTimeoutRef.current) {
      clearTimeout(networkErrorTimeoutRef.current);
      networkErrorTimeoutRef.current = null;
    }
    
    clearWatchdog();
  }, [clearWatchdog]);

  const handleNetworkErrorTimeout = useCallback(() => {
    console.error("Network error timed out.");
    cleanupPlayer();

    if (retryCount < MAX_RETRIES) {
      console.log(`Retrying... Attempt ${retryCount + 1} of ${MAX_RETRIES}`);
      setRetryCount((prev) => prev + 1);
    } else {
      setErrorMessage(DEFAULT_ERROR);
      setIsLiveFeedActive(false);
      setIsLoading(false);
    }
  }, [retryCount, cleanupPlayer, setRetryCount, setErrorMessage, setIsLiveFeedActive, setIsLoading]);

  const initPlayer = useCallback((url) => {
    const video = videoRef.current;
    if (!video) {
      setErrorMessage("Video element not found.");
      setIsLoading(false);
      return;
    }

    cleanupPlayer();
    
    hlsRef.current = new Hls();
    hlsRef.current.attachMedia(video);
    
    hlsRef.current.on(Hls.Events.MEDIA_ATTACHED, () => {
      console.log("Media attached, loading source:", url);
      hlsRef.current.loadSource(url);

      // Start watchdog
      clearWatchdog();
      watchdogTimeoutRef.current = setTimeout(() => {
        console.error("Stream timeout: no playback or error within 5s.");
        handleNetworkErrorTimeout();
      }, WATCHDOG_TIMEOUT_MS);
    });

    hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
      video
        .play()
        .then(() => {
          clearWatchdog();
          setIsLoading(false);
          setIsLiveFeedActive(true);
          setRetryCount(0);
          
          if (networkErrorTimeoutRef.current) {
            clearTimeout(networkErrorTimeoutRef.current);
            networkErrorTimeoutRef.current = null;
          }
        })
        .catch((err) => {
          console.error("Autoplay prevented:", err);
          setIsLoading(false);
          setIsLiveFeedActive(false);
          setErrorMessage("Autoplay failed.");
        });
    });

    hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
      console.error("HLS error:", data);
      clearWatchdog();
      
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.MEDIA_ERROR:
            hlsRef.current.recoverMediaError();
            break;

          case Hls.ErrorTypes.NETWORK_ERROR:
            if (!networkErrorTimeoutRef.current) {
              networkErrorTimeoutRef.current = setTimeout(
                handleNetworkErrorTimeout,
                RETRY_DELAY_MS
              );
            }
            hlsRef.current.startLoad();
            break;

          default:
            cleanupPlayer();
            setErrorMessage(DEFAULT_ERROR);
            setIsLiveFeedActive(false);
            setIsLoading(false);
            break;
        }
      }
    });
  }, [videoRef, cleanupPlayer, clearWatchdog, handleNetworkErrorTimeout, setErrorMessage, setIsLoading, setIsLiveFeedActive, setRetryCount]);

  return { initPlayer, cleanupPlayer };
};

const LiveFeedPreview = () => {
  const [feedData, setFeedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR);
  const [isLiveFeedActive, setIsLiveFeedActive] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const videoRef = useRef(null);
  const authService = AuthenticationService.getInstance();
  const parentRoute = authService.getMasterParentRoute();
  const location = useLocation();
  const navigate = useNavigate();

  const { initPlayer, cleanupPlayer } = useHlsPlayer(
    videoRef,
    retryCount,
    setRetryCount,
    setErrorMessage,
    setIsLiveFeedActive,
    setIsLoading
  );

  const handleRetry = useCallback(() => {
    if (!feedData?.rtmpFeedUrl) {
      setErrorMessage("Live feed URL not provided.");
      return;
    }
    setIsLoading(true);
    setIsLiveFeedActive(false);
    setErrorMessage(null);
    cleanupPlayer();
    initPlayer(feedData.rtmpFeedUrl);
  }, [feedData, cleanupPlayer, initPlayer]);

  const handleNavigation = useCallback(() => {
    const route = `${parentRoute}/cameraDetails`;
    navigate(route, { state: { ...feedData, liveFeed: isLiveFeedActive } });
  }, [navigate, feedData, isLiveFeedActive, authService]);

  useEffect(() => {
    const prevData = location?.state?.feedData;
    if (prevData) {
      setFeedData(prevData);
      setErrorMessage(null);
    } else {
      setErrorMessage("No feed data provided.");
    }
    setIsLoading(false);
  }, [location]);

  useEffect(() => {
    return cleanupPlayer;
  }, [cleanupPlayer]);

  useEffect(() => {
    if (!feedData?.rtmpFeedUrl) {
      setErrorMessage("Live feed URL not provided.");
      setIsLoading(false);
      setIsLiveFeedActive(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setIsLiveFeedActive(false);
    initPlayer(feedData.rtmpFeedUrl);
  }, [feedData, retryCount, initPlayer]);

  return (
    <div className="camera-wrapper row">
      {isLoading && <ChaloLoader />}
      <div className="camera-details-container">
        <ChaloTopNav navContent="Required information" />
        <div className="col-md-12 camera-detail-content">
          <h4 className="feed-heading">RTMP live feed</h4>
          <div className="w-100 live-feed" style={{ position: "relative" }}>
            <video
              ref={videoRef}
              className="live-feed-canva"
              playsInline
              muted
              autoPlay
              controls={false}
              width="100%"
            />
            {!isLiveFeedActive && !isLoading && (
              <div className="canva-failed">
                <p>{errorMessage}</p>
                <button className="retry-button" onClick={handleRetry}>
                  <i className="fa fa-rotate-right"></i> Retry
                </button>
              </div>
            )}
          </div>

          <div
            className={`feed-msg ${
              isLiveFeedActive ? "feed-success-msg" : "feed-failed-msg"
            }`}
          >
            {isLiveFeedActive ? (
              <>
                <i className="fa fa-check-circle"></i>
                <span>RTMP feed success</span>
              </>
            ) : (
              <>
                <i
                  className="fa fa-exclamation-triangle"
                  aria-hidden="true"
                ></i>
                <span>RTMP feed failed</span>
              </>
            )}
          </div>
        </div>

        <div className="col-md-12 live-feed-footer">
          <ChaloButton buttonText="Proceed" onClick={handleNavigation} />
        </div>
      </div>
    </div>
  );
};

export default LiveFeedPreview;